import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { api } from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import CatalogCellModal from './CatalogCellModal';
import MatricolaExportModal from './MatricolaExportModal';
import CatalogExportModal from './CatalogExportModal';

type CatalogStructure = 'semestru' | 'trimestru' | 'module';
type EducationLevel = 'primar' | 'gimnazial' | 'liceal' | 'universitar';

type GradebookConfig = {
  id: number;
  classroom_id: number;
  school_year: string;
  structure: CatalogStructure;
  education_level: EducationLevel | string;
  locked: boolean;
  period_index?: number | null;
};

type CatalogStudent = {
  id: number;
  full_name: string;
  email?: string | null;
};

type CatalogSubject = {
  id: number;
  name: string;
  professor_id?: number | null;
  professor_name?: string | null;
  gradebook_config_id?: number | null;
  period_index?: number | null;
};

type CatalogGrade = {
  id: number;
  student_id: number;
  subject_id: number;
  value: number | null;
  qualifier: string | null;
  date: string | null;
  created_at?: string | null;
  teacher_id?: number | null;
  period_index: number | null;
  is_thesis?: boolean;
  is_homework?: boolean;
  is_conduct?: boolean;
  is_final?: boolean;
  is_lab?: boolean;
  is_seminar?: boolean;
  category_id?: number | null;
  category_name?: string | null;
  comment?: string | null;
};

type ConductGrade = {
  id: number;
  student_id: number;
  value: number | null;
  qualifier: string | null;
  teacher_id?: number | null;
  date: string | null;
  created_at?: string | null;
  period_index: number | null;
  comment?: string | null;
};

type CatalogAbsence = {
  student_id: number;
  subject_id: number;
  total: number;
  motivated: number;
};

type CatalogOverview = {
  config: GradebookConfig;
  period_index: number | null;
  students: CatalogStudent[];
  subjects: CatalogSubject[];
  grades: CatalogGrade[];
  absences: CatalogAbsence[];
  conduct_grades?: ConductGrade[];
};

type EvolutionStatus = 'bine' | 'binisor' | 'avertisment' | 'fara_date';
type EvolutionTrend = 'up' | 'down' | 'flat';
type Evolution = {
  status: EvolutionStatus;
  trend: EvolutionTrend;
  avg: number | null;
  absTotal: number;
  absMotivated: number;
};

type SubjectView = 'all' | 'my' | 'other';
type SubjectMenuAction = 'rename' | 'delete';

function gradeToNumberForIndicator(
  g: Pick<CatalogGrade, 'value' | 'qualifier'>,
  educationLevel: string
): number | null {
  if (typeof g.value === 'number') return g.value;
  const lvl = (educationLevel || '').toString().trim().toLowerCase();
  if (lvl !== 'primar') return null;
  const q = (g.qualifier || '').toString().trim().toUpperCase();
  if (!q) return null;
  if (q === 'FB') return 10;
  if (q === 'B') return 8;
  if (q === 'S') return 6;
  if (q === 'I') return 4;
  return null;
}

function avgOf(nums: number[]): number | null {
  if (!nums.length) return null;
  const a = nums.reduce((s, v) => s + v, 0) / nums.length;
  return Math.round(a * 100) / 100;
}

function generateSchoolYears(): string[] {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 4 }, (_, i) => {
    const start = currentYear - 1 + i;
    return `${start}-${start + 1}`;
  });
}

function periodLabels(structure: CatalogStructure): string[] {
  if (structure === 'semestru') return ['Semestrul 1', 'Semestrul 2'];
  if (structure === 'trimestru') return ['Trimestrul 1', 'Trimestrul 2', 'Trimestrul 3'];
  return ['Modul 1', 'Modul 2', 'Modul 3', 'Modul 4', 'Modul 5'];
}

interface Props {
  classroomId: number;
  initialSchoolYear?: string | null;
}

const CatalogPanel: React.FC<Props> = ({ classroomId, initialSchoolYear = null }) => {
  const { me } = useAuth();
  const role = String(me?.role || '').toLowerCase();
  const canManageConfig = ['profesor', 'admin', 'secretariat'].includes(role);
  const canEditEntries = ['profesor', 'admin'].includes(role);
  const years = useMemo(() => generateSchoolYears(), []);
  const [configs, setConfigs] = useState<GradebookConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [overview, setOverview] = useState<CatalogOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  const [cellOpen, setCellOpen] = useState(false);
  const [cellStudentId, setCellStudentId] = useState<number | null>(null);
  const [cellSubjectId, setCellSubjectId] = useState<number | null>(null); // null => purtare
  const [exportOpen, setExportOpen] = useState(false);
  const [classExportOpen, setClassExportOpen] = useState(false);

  const [schoolYear, setSchoolYear] = useState('');
  const [structure, setStructure] = useState<CatalogStructure | ''>('');
  const [level, setLevel] = useState<EducationLevel | ''>('');

  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);
  const selectedConfig = useMemo(
    () => configs.find((c) => c.id === selectedConfigId) || null,
    [configs, selectedConfigId]
  );
  const isReadOnly = Boolean(selectedConfig?.locked) || !canEditEntries;

  const [periodIndex, setPeriodIndex] = useState(0);
  const [mobileStudentOpenId, setMobileStudentOpenId] = useState<number | null>(null);

  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<GradebookConfig | null>(null);
  const [editYear, setEditYear] = useState('');
  const [editStructure, setEditStructure] = useState<CatalogStructure>('semestru');
  const [editLevel, setEditLevel] = useState<EducationLevel>('primar');

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<GradebookConfig | null>(null);
  const [deletePassword, setDeletePassword] = useState('');

  useEffect(() => {
    const onDocClick = () => setOpenMenuId(null);
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const openEdit = (cfg: GradebookConfig) => {
    setEditTarget(cfg);
    setEditYear(String(cfg.school_year || '').trim());
    setEditStructure(((cfg.structure || '').toString().trim().toLowerCase() as any) || 'semestru');
    setEditLevel(((cfg.education_level || '').toString().trim().toLowerCase() as any) || 'primar');
    setEditOpen(true);
  };

  const openDelete = (cfg: GradebookConfig) => {
    setDeleteTarget(cfg);
    setDeletePassword('');
    setDeleteOpen(true);
  };

  const submitEdit = async () => {
    if (!editTarget) return;
    if (!editYear.trim()) {
      setError('Anul școlar este obligatoriu.');
      return;
    }
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await api.put(`/api/catalog/configurare/${editTarget.id}`, {
        school_year: editYear.trim(),
        structure: editStructure,
        education_level: editLevel,
      });
      setInfo('Catalog actualizat.');
      setEditOpen(false);
      await load();
      setSelectedConfigId(editTarget.id);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Nu am putut modifica catalogul.');
    } finally {
      setSaving(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    if (!deletePassword.trim()) {
      setError('Parola este obligatorie pentru ștergere.');
      return;
    }
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await api.delete(`/api/catalog/configurare/${deleteTarget.id}`, { data: { password: deletePassword } });
      setInfo('Catalog șters.');
      setDeleteOpen(false);
      await load();
      setSelectedConfigId((prev) => (prev === deleteTarget.id ? null : prev));
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Nu am putut șterge catalogul.');
    } finally {
      setSaving(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/catalog/config/all/${classroomId}`);
      setConfigs(res.data);
      if (Array.isArray(res.data) && res.data.length) {
        if (initialSchoolYear && String(initialSchoolYear).trim()) {
          const match = res.data.find((c: any) => String(c?.school_year || '').trim() === String(initialSchoolYear).trim());
          if (match?.id) {
            setSelectedConfigId(Number(match.id));
            return;
          }
        }
        if (!selectedConfigId) setSelectedConfigId(res.data[0].id);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Nu s-au putut încărca cataloagele.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classroomId]);

  useEffect(() => {
    if (!selectedConfig) return;
    setPeriodIndex(0);
  }, [selectedConfig?.id]);

  const loadOverview = async (cfgId: number, pIdx: number) => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const res = await api.get(`/api/catalog/classroom/${classroomId}/overview`, {
        params: { config_id: cfgId, period_index: pIdx },
      });
      setOverview(res.data);
    } catch (err: any) {
      setOverview(null);
      setOverviewError(err?.response?.data?.detail || 'Nu s-a putut încărca catalogul.');
    } finally {
      setOverviewLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedConfigId) return;
    void loadOverview(selectedConfigId, periodIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConfigId, periodIndex, classroomId]);

  useEffect(() => {
    setMobileStudentOpenId(null);
  }, [selectedConfigId, periodIndex, classroomId]);

  const gradeText = (g: CatalogGrade) => {
    const base = g.value != null ? String(g.value) : g.qualifier || '';
    if (!base) return '';
    return g.is_thesis ? `${base}*` : base;
  };

  const gradeBadgeMeta = (g: CatalogGrade) => {
    if (g.is_final) {
      return { short: 'FIN', title: 'Notă finală', cls: 'bg-success/10 border-success/30 text-success' };
    }
    if (g.is_lab) {
      return { short: 'LAB', title: 'Notă laborator', cls: 'bg-accent/10 border-accent/30 text-accent' };
    }
    if (g.is_thesis) {
      return { short: 'TEZ', title: 'Teză', cls: 'bg-warning/10 border-warning/30 text-warning' };
    }
    if (g.is_seminar) {
      return { short: 'SEM', title: 'Seminar', cls: 'bg-primary/10 border-primary/30 text-primary' };
    }
    if (g.is_homework) {
      return { short: 'TEM', title: 'Temă', cls: 'bg-muted/40 border-border text-foreground' };
    }
    return { short: '', title: 'Notă', cls: 'bg-muted/40 border-border text-foreground' };
  };

  const gradeBadgeValue = (g: CatalogGrade) => {
    const base = g.value != null ? String(g.value) : (g.qualifier || '').toString();
    return base.trim();
  };

  const gradesByCell = useMemo(() => {
    const map = new Map<string, CatalogGrade[]>();
    for (const g of overview?.grades || []) {
      if (!g.subject_id) continue;
      const key = `${g.student_id}:${g.subject_id}`;
      const arr = map.get(key);
      if (arr) arr.push(g);
      else map.set(key, [g]);
    }
    return map;
  }, [overview?.grades]);

  const conductByStudent = useMemo(() => {
    const map = new Map<number, ConductGrade>();
    for (const g of overview?.conduct_grades || []) {
      if (!map.has(g.student_id)) map.set(g.student_id, g);
    }
    return map;
  }, [overview?.conduct_grades]);

  const absByCell = useMemo(() => {
    const map = new Map<string, CatalogAbsence>();
    for (const a of overview?.absences || []) {
      const key = `${a.student_id}:${a.subject_id}`;
      map.set(key, a);
    }
    return map;
  }, [overview?.absences]);

  const evolutionByStudent = useMemo(() => {
    const result = new Map<number, Evolution>();
    if (!overview) return result;

    const educationLevel = (selectedConfig?.education_level || overview.config.education_level || '').toString();
    const gradesByStudent = new Map<number, CatalogGrade[]>();
    const absByStudent = new Map<number, { total: number; motivated: number }>();

    for (const g of overview.grades || []) {
      if (g.is_conduct) continue;
      const arr = gradesByStudent.get(g.student_id);
      if (arr) arr.push(g);
      else gradesByStudent.set(g.student_id, [g]);
    }

    for (const a of overview.absences || []) {
      const cur = absByStudent.get(a.student_id) || { total: 0, motivated: 0 };
      cur.total += a.total || 0;
      cur.motivated += a.motivated || 0;
      absByStudent.set(a.student_id, cur);
    }

    for (const st of overview.students) {
      const grades = gradesByStudent.get(st.id) || [];
      const abs = absByStudent.get(st.id) || { total: 0, motivated: 0 };

      const nums = grades
        .map((g) => gradeToNumberForIndicator(g, educationLevel))
        .filter((v): v is number => typeof v === 'number');
      const avg = avgOf(nums);

      const status: EvolutionStatus = (() => {
        if (avg == null) {
          if (abs.total === 0) return 'fara_date';
          if (abs.total <= 2) return 'binisor';
          return 'avertisment';
        }
        if (avg >= 8.5 && abs.total <= 2) return 'bine';
        if (avg >= 6.5 && abs.total <= 6) return 'binisor';
        return 'avertisment';
      })();

      const trend: EvolutionTrend = (() => {
        const withDate = grades
          .map((g) => {
            const raw = (g.date || g.created_at || '').toString();
            if (!raw) return null;
            const d = new Date(raw);
            if (Number.isNaN(d.getTime())) return null;
            const v = gradeToNumberForIndicator(g, educationLevel);
            if (v == null) return null;
            return { d, v };
          })
          .filter((x): x is { d: Date; v: number } => !!x)
          .sort((a, b) => a.d.getTime() - b.d.getTime());

        if (withDate.length < 2) return 'flat';

        const splitIndex = Math.floor(withDate.length / 2);
        const first = withDate.slice(0, splitIndex).map((x) => x.v);
        const second = withDate.slice(splitIndex).map((x) => x.v);
        const a1 = avgOf(first);
        const a2 = avgOf(second);
        if (a1 == null || a2 == null) return 'flat';
        const delta = a2 - a1;
        if (delta > 0.3) return 'up';
        if (delta < -0.3) return 'down';
        return 'flat';
      })();

      result.set(st.id, {
        status,
        trend,
        avg,
        absTotal: abs.total,
        absMotivated: abs.motivated,
      });
    }

    return result;
  }, [overview, selectedConfig?.education_level]);

  const selectedStudent = useMemo(() => {
    if (!overview || !cellStudentId) return null;
    return overview.students.find((s) => s.id === cellStudentId) || null;
  }, [overview, cellStudentId]);

  const selectedSubject = useMemo(() => {
    if (!overview) return null;
    if (cellSubjectId == null) return null;
    return overview.subjects.find((s) => s.id === cellSubjectId) || null;
  }, [overview, cellSubjectId]);

  const subjectGroups = useMemo(() => {
    const subjects = overview?.subjects || [];
    const meId = me?.id != null ? Number(me.id) : null;
    const isProfesor = String(me?.role || '').toLowerCase() === 'profesor';

    if (!isProfesor || !meId) return { my: [] as CatalogSubject[], other: subjects };

    const myFromMe = new Set<number>(
      (me?.subjects || [])
        .filter((s) => Number(s.classroom_id) === Number(classroomId))
        .map((s) => Number(s.id))
    );

    const my = subjects.filter((s) => Number(s.professor_id) === meId || myFromMe.has(Number(s.id)));
    const myIds = new Set(my.map((s) => s.id));
    const other = subjects.filter((s) => !myIds.has(s.id));
    return { my, other };
  }, [overview?.subjects, me?.id, me?.role, me?.subjects, classroomId]);

  const orderedSubjects = useMemo(() => {
    if (!overview) return [] as CatalogSubject[];
    return [...subjectGroups.my, ...subjectGroups.other];
  }, [overview, subjectGroups.my, subjectGroups.other]);

  const showSubjectLabelsRow = useMemo(() => {
    const isProfesor = String(me?.role || '').toLowerCase() === 'profesor';
    return isProfesor && subjectGroups.my.length > 0;
  }, [me?.role, subjectGroups.my.length]);

  const [subjectView, setSubjectView] = useState<SubjectView>('all');
  const [addSubjectOpen, setAddSubjectOpen] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [addingSubject, setAddingSubject] = useState(false);
  const [subjectActionError, setSubjectActionError] = useState<string | null>(null);

  const [openSubjectMenuId, setOpenSubjectMenuId] = useState<number | null>(null);

  useEffect(() => {
    if (!showSubjectLabelsRow && subjectView !== 'all') {
      setSubjectView('all');
    }
  }, [showSubjectLabelsRow, subjectView]);

  useEffect(() => {
    if (!addSubjectOpen) return;
    setSubjectActionError(null);
  }, [addSubjectOpen]);

  useEffect(() => {
    if (openSubjectMenuId == null) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest(`[data-subject-menu-root="${openSubjectMenuId}"]`)) return;
      setOpenSubjectMenuId(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openSubjectMenuId]);

  const mySubjectIds = useMemo(() => new Set(subjectGroups.my.map((s) => s.id)), [subjectGroups.my]);

  const displayedSubjects = useMemo(() => {
    if (!overview) return [] as CatalogSubject[];
    if (!showSubjectLabelsRow) return orderedSubjects;
    if (subjectView === 'my') return subjectGroups.my;
    if (subjectView === 'other') return subjectGroups.other;
    return orderedSubjects;
  }, [overview, showSubjectLabelsRow, subjectView, subjectGroups.my, subjectGroups.other, orderedSubjects]);

  const refreshOverview = async () => {
    if (!selectedConfigId) return;
    await loadOverview(selectedConfigId, periodIndex);
  };

  const handleAddSubject = async () => {
    if (isReadOnly) return;
    if (!selectedConfigId || !selectedConfig) return;
    const name = newSubjectName.trim();
    if (!name) {
      setSubjectActionError('Completează denumirea materiei.');
      return;
    }
    setAddingSubject(true);
    setSubjectActionError(null);
    try {
      await api.post('/api/materii/subjects', {
        name,
        classroom_id: classroomId,
        gradebook_config_id: selectedConfigId,
        period_index: periodIndex,
      });
      setNewSubjectName('');
      setAddSubjectOpen(false);
      setSubjectView('my');
      await refreshOverview();
    } catch (err: any) {
      setSubjectActionError(err?.response?.data?.detail || 'Nu s-a putut adăuga materia.');
    } finally {
      setAddingSubject(false);
    }
  };

  const handleRenameSubject = async (subject: CatalogSubject) => {
    if (isReadOnly) return;
    const next = window.prompt('Denumire noua materie:', subject.name);
    if (next == null) return;
    const name = next.trim();
    if (!name) return;
    setSubjectActionError(null);
    try {
      await api.patch(`/api/materii/subjects/${subject.id}`, { name });
      setOpenSubjectMenuId(null);
      await refreshOverview();
    } catch (err: any) {
      setSubjectActionError(err?.response?.data?.detail || 'Nu s-a putut redenumi materia.');
    }
  };

  const handleDeleteSubject = async (subject: CatalogSubject) => {
    if (isReadOnly) return;
    const ok = window.confirm(`Stergi materia \"${subject.name}\"?`);
    if (!ok) return;
    setSubjectActionError(null);
    try {
      await api.delete(`/api/materii/subjects/${subject.id}`);
      setOpenSubjectMenuId(null);
      await refreshOverview();
    } catch (err: any) {
      setSubjectActionError(err?.response?.data?.detail || 'Nu s-a putut șterge materia.');
    }
  };

  const openCell = (studentId: number, subjectId: number | null) => {
    if (isReadOnly) return;
    setCellStudentId(studentId);
    setCellSubjectId(subjectId);
    setCellOpen(true);
  };

  const createConfig = async () => {
    if (!schoolYear || !structure || !level) {
      setError('Completează anul școlar, structura și nivelul.');
      return;
    }

    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.post('/api/catalog/configurare', {
        classroom_id: classroomId,
        school_year: schoolYear,
        structure,
        education_level: level,
      });
      setInfo('Catalogul a fost creat.');
      setSchoolYear('');
      setStructure('');
      setLevel('');
      await load();
      setSelectedConfigId(res.data?.id ?? null);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Eroare la crearea catalogului.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Catalog</h2>
            <p className="text-sm text-muted-foreground">
              Configurează cataloage pe ani școlari și structură (semestru/trimestru/module).
            </p>
          </div>
          <Button variant="outline" iconName="RefreshCcw" iconPosition="left" onClick={load} disabled={loading}>
            Reîncarcă
          </Button>
        </div>

        {selectedConfig?.locked ? (
          <div className="mt-3 bg-muted border border-border rounded-md p-3 text-sm text-foreground">
            Catalog arhivat: este <span className="font-medium">read-only</span> (nu se mai pot adăuga/modifica note sau absențe).
          </div>
        ) : null}

        {error && (
          <div className="mt-3 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{error}</div>
        )}
        {info && (
          <div className="mt-3 bg-success/10 border border-success/20 text-success rounded-md p-3 text-sm">{info}</div>
        )}

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-4 gap-3">
            <label className="block text-sm font-medium text-foreground">
              An școlar
              <select
                value={schoolYear}
                onChange={(e) => setSchoolYear(e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">-- selectează --</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm font-medium text-foreground">
            Structură
            <select
              value={structure}
              onChange={(e) => setStructure(e.target.value as CatalogStructure)}
              className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">-- selectează --</option>
              <option value="semestru">Semestru</option>
              <option value="trimestru">Trimestru</option>
              <option value="module">Module</option>
            </select>
          </label>

          <label className="block text-sm font-medium text-foreground">
            Nivel
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value as EducationLevel)}
              className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">-- selectează --</option>
              <option value="primar">Primar</option>
              <option value="gimnazial">Gimnazial</option>
              <option value="liceal">Liceal</option>
              <option value="universitar">Universitar</option>
            </select>
          </label>

          <div className="flex items-end">
            <Button
              variant="default"
              fullWidth
              iconName="Plus"
              iconPosition="left"
              onClick={createConfig}
              disabled={saving || !canManageConfig}
              title={!canManageConfig ? 'Doar profesorii, adminii sau secretariatul pot crea cataloage.' : undefined}
            >
              Creează
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-foreground">Cataloagele clasei</h3>
          <div className="text-xs text-muted-foreground">{configs.length} configurări</div>
        </div>

        {configs.length === 0 ? (
          <div className="text-sm text-muted-foreground mt-3">Nu există încă un catalog configurat.</div>
        ) : (
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
            {configs.map((c) => (
              <div
                key={c.id}
                className={`text-left border rounded-lg p-3 transition-colors duration-150 cursor-pointer ${
                  selectedConfigId === c.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'
                }`}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedConfigId(c.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setSelectedConfigId(c.id);
                }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-foreground truncate">{c.school_year}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {c.structure} • {c.education_level}
                    </div>
                  </div>

                  {c.locked ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-foreground border border-border">
                      Arhivat
                    </span>
                  ) : null}

                  <div className="relative flex items-center gap-1 text-muted-foreground">
                    {canEditEntries && (
                      <button
                        type="button"
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted"
                        aria-label="Acțiuni catalog"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setOpenMenuId((prev) => (prev === c.id ? null : c.id));
                        }}
                      >
                        <Icon name="MoreVertical" size={18} />
                      </button>
                    )}
                    <Icon name="ChevronRight" size={18} />

                    {canEditEntries && openMenuId === c.id && !c.locked && (
                      <div
                        className="absolute right-0 top-9 z-50 w-44 rounded-md border border-border bg-card shadow-md overflow-hidden"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                          onClick={() => {
                            setOpenMenuId(null);
                            openEdit(c);
                          }}
                        >
                          Modifică
                        </button>
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm text-error hover:bg-muted"
                          onClick={() => {
                            setOpenMenuId(null);
                            openDelete(c);
                          }}
                        >
                          Șterge
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editOpen && editTarget && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
          onClick={() => (!saving ? setEditOpen(false) : null)}
        >
          <div
            className="w-full max-w-lg bg-card border border-border rounded-lg p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-sm font-semibold text-foreground">Modifică catalog</div>
                <div className="text-xs text-muted-foreground">
                  Se poate modifica doar dacă este gol (fără note/absențe/materii).
                </div>
              </div>
              <button
                type="button"
                className="h-8 w-8 rounded-md hover:bg-muted inline-flex items-center justify-center"
                onClick={() => (!saving ? setEditOpen(false) : null)}
                aria-label="Închide"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="text-xs text-muted-foreground">
                An școlar
                <select
                  className="mt-1 w-full border border-border rounded-md bg-background px-3 py-2 text-sm text-foreground"
                  value={editYear}
                  onChange={(e) => setEditYear(e.target.value)}
                >
                  <option value="">-- selectează --</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-xs text-muted-foreground">
                Structură
                <select
                  className="mt-1 w-full border border-border rounded-md bg-background px-3 py-2 text-sm text-foreground"
                  value={editStructure}
                  onChange={(e) => setEditStructure(e.target.value as any)}
                >
                  <option value="semestru">Semestru</option>
                  <option value="trimestru">Trimestru</option>
                  <option value="module">Module</option>
                </select>
              </label>

              <label className="text-xs text-muted-foreground">
                Nivel
                <select
                  className="mt-1 w-full border border-border rounded-md bg-background px-3 py-2 text-sm text-foreground"
                  value={editLevel}
                  onChange={(e) => setEditLevel(e.target.value as any)}
                >
                  <option value="primar">Primar</option>
                  <option value="gimnazial">Gimnazial</option>
                  <option value="liceal">Liceal</option>
                  <option value="universitar">Universitar</option>
                </select>
              </label>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>
                Renunță
              </Button>
              <Button variant="default" onClick={submitEdit} disabled={saving}>
                Salvează
              </Button>
            </div>
          </div>
        </div>
      )}

      {deleteOpen && deleteTarget && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
          onClick={() => (!saving ? setDeleteOpen(false) : null)}
        >
          <div
            className="w-full max-w-lg bg-card border border-border rounded-lg p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-sm font-semibold text-foreground">Șterge catalog</div>
                <div className="text-xs text-muted-foreground">
                  Se poate șterge doar dacă este gol (fără note/absențe/materii).
                </div>
              </div>
              <button
                type="button"
                className="h-8 w-8 rounded-md hover:bg-muted inline-flex items-center justify-center"
                onClick={() => (!saving ? setDeleteOpen(false) : null)}
                aria-label="Închide"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="text-sm text-foreground mb-3">
              Confirmi ștergerea catalogului <strong>{deleteTarget.school_year}</strong> ({deleteTarget.structure})?
            </div>

            <label className="text-xs text-muted-foreground">
              Parola contului
              <input
                type="password"
                className="mt-1 w-full border border-border rounded-md bg-background px-3 py-2 text-sm text-foreground"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Introdu parola"
              />
            </label>

            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={saving}>
                Renunță
              </Button>
              <Button variant="destructive" onClick={submitDelete} disabled={saving}>
                Șterge
              </Button>
            </div>
          </div>
        </div>
      )}

      {selectedConfig && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Catalog selectat</h3>
              <p className="text-xs text-muted-foreground">
                {selectedConfig.school_year} • {selectedConfig.structure} • {selectedConfig.education_level}
              </p>
            </div>
            {selectedConfigId && overview?.students?.length ? (
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Button
                  variant="outline"
                  size="xs"
                  iconName="FileDown"
                  iconPosition="left"
                  onClick={() => setClassExportOpen(true)}
                >
                  Export catalog
                </Button>
                <Button
                  variant="outline"
                  size="xs"
                  iconName="FileDown"
                  iconPosition="left"
                  onClick={() => setExportOpen(true)}
                >
                  Foaie matricola
                </Button>
              </div>
            ) : null}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {periodLabels(selectedConfig.structure).map((label, idx) => (
              <button
                key={label}
                onClick={() => setPeriodIndex(idx)}
                className={`px-3 py-1.5 rounded-md text-sm border transition-colors duration-150 ${
                  periodIndex === idx ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:bg-muted'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-4 bg-muted/30 border border-border rounded-md p-3 text-sm text-muted-foreground flex items-start gap-2">
            <Icon name="Info" size={18} className="mt-0.5" />
            <div>
              Notele/absențele sunt afișate mai jos pentru perioada selectată (index {periodIndex}).
            </div>
          </div>

          <div className="mt-4">
            {overviewError && (
              <div className="mb-3 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{overviewError}</div>
            )}

            {overviewLoading ? (
              <div className="text-sm text-muted-foreground">Se încarcă datele catalogului…</div>
            ) : !overview ? (
              <div className="text-sm text-muted-foreground">Nu există date de afișat.</div>
            ) : overview.students.length === 0 ? (
              <div className="text-sm text-muted-foreground">Nu există elevi în această clasă.</div>
            ) : (
              <>
                {String(me?.role || '').toLowerCase() === 'profesor' && (
                  <>
                  <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <button
                      type="button"
                      onClick={() => setSubjectView((v) => (v === 'my' ? 'all' : 'my'))}
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 transition ${
                        subjectView === 'my'
                          ? 'border-primary/30 bg-primary/10 text-primary'
                          : 'border-primary/20 bg-primary/5 text-primary hover:bg-primary/10'
                      }`}
                      title="Afișează doar materiile mele (click din nou = toate)"
                    >
                      Materiile mele <span className="ml-1 text-[10px] opacity-70">({subjectGroups.my.length})</span>
                    </button>
                    {subjectGroups.other.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSubjectView((v) => (v === 'other' ? 'all' : 'other'))}
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 transition ${
                          subjectView === 'other'
                            ? 'border-border bg-muted/40 text-foreground'
                            : 'border-border bg-background text-foreground hover:bg-muted/30'
                        }`}
                        title="Afișează doar alte materii (click din nou = toate)"
                      >
                        Alte materii <span className="ml-1 text-[10px] opacity-70">({subjectGroups.other.length})</span>
                      </button>
                    )}

                    {String(me?.role || '').toLowerCase() === 'profesor' && (
                      <Button
                        className="ml-auto"
                        variant="outline"
                        size="xs"
                        iconName="Plus"
                        iconPosition="left"
                        onClick={() => setAddSubjectOpen((v) => !v)}
                      >
                        Adauga materie
                      </Button>
                    )}
                  </div>

                  {addSubjectOpen && (
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <input
                        value={newSubjectName}
                        onChange={(e) => setNewSubjectName(e.target.value)}
                        placeholder="Denumire materie (ex: Fizica)"
                        className="h-8 w-[260px] rounded-md border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <Button size="xs" loading={addingSubject} onClick={handleAddSubject}>
                        Salveaza
                      </Button>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => {
                          setAddSubjectOpen(false);
                          setNewSubjectName('');
                        }}
                        disabled={addingSubject}
                      >
                        Renunta
                      </Button>
                    </div>
                  )}

                  {subjectActionError && (
                    <div className="mb-3 bg-error/10 border border-error/20 text-error rounded-md p-2 text-sm">
                      {subjectActionError}
                    </div>
                  )}
                  </>
                )}

                {selectedConfigId && overview?.students?.length ? (
                  <MatricolaExportModal
                    open={exportOpen}
                    onClose={() => setExportOpen(false)}
                    students={overview.students}
                    configId={selectedConfigId}
                    schoolYearLabel={selectedConfig?.school_year || null}
                  />
                ) : null}

                {selectedConfigId ? (
                  <CatalogExportModal
                    open={classExportOpen}
                    onClose={() => setClassExportOpen(false)}
                    classroomId={classroomId}
                    configId={selectedConfigId}
                    periodIndex={Number.isFinite(periodIndex) ? periodIndex : null}
                    classLabel={null}
                    schoolYearLabel={selectedConfig?.school_year || null}
                  />
                ) : null}
                <div className="md:hidden space-y-2">
                  {overview.students.map((st) => {
                    const isOpen = mobileStudentOpenId === st.id;
                    const ev = evolutionByStudent.get(st.id) || null;
                    const conduct = conductByStudent.get(st.id) || null;

                    const evUi = !ev
                      ? { label: 'Fără date', badge: 'bg-muted/40 border-border text-muted-foreground' }
                      : ev.status === 'bine'
                      ? { label: 'Bine', badge: 'bg-success/10 border-success/30 text-success' }
                      : ev.status === 'binisor'
                      ? { label: 'Binișor', badge: 'bg-warning/10 border-warning/30 text-warning' }
                      : ev.status === 'avertisment'
                      ? { label: 'Avertisment', badge: 'bg-error/10 border-error/30 text-error' }
                      : { label: 'Fără date', badge: 'bg-muted/40 border-border text-muted-foreground' };

                    const trendIcon = !ev ? 'Minus' : ev.trend === 'up' ? 'TrendingUp' : ev.trend === 'down' ? 'TrendingDown' : 'Minus';
                    const trendColor =
                      !ev ? 'text-muted-foreground' : ev.trend === 'up' ? 'text-success' : ev.trend === 'down' ? 'text-error' : 'text-muted-foreground';

                    const evDetail = ev
                      ? [
                          ev.avg != null ? `Medie: ${ev.avg}` : null,
                          `Absențe: ${ev.absTotal}` + (ev.absMotivated ? ` (motivate: ${ev.absMotivated})` : ''),
                        ]
                          .filter(Boolean)
                          .join(' · ')
                      : null;

                    return (
                      <div key={st.id} className="border border-border rounded-lg overflow-hidden bg-background">
                        <button
                          type="button"
                          className="w-full text-left px-3 py-3 hover:bg-muted/30 transition-colors"
                          onClick={() => setMobileStudentOpenId((v) => (v === st.id ? null : st.id))}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="font-semibold text-foreground truncate">{st.full_name}</div>
                              {st.email ? <div className="text-xs text-muted-foreground truncate">{st.email}</div> : null}
                              {evDetail ? (
                                <div className="text-xs text-muted-foreground mt-1 truncate" title={evDetail}>
                                  {evDetail}
                                </div>
                              ) : null}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${evUi.badge}`}>
                                {evUi.label}
                              </span>
                              <Icon name={trendIcon} size={16} className={trendColor} />
                              <Icon
                                name="ChevronDown"
                                size={18}
                                className={`text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
                              />
                            </div>
                          </div>
                        </button>

                        {isOpen ? (
                          <div className="px-3 pb-3">
                            <div className="space-y-3">
                              <div className="space-y-2">
                                {displayedSubjects.map((sub) => {
                                  const key = `${st.id}:${sub.id}`;
                                  const grades = gradesByCell.get(key) || [];
                                  const abs = absByCell.get(key);
                                  const orderedGrades = grades
                                    .slice()
                                    .sort(
                                      (a, b) =>
                                        new Date((a.date || a.created_at || '') as string).getTime() -
                                        new Date((b.date || b.created_at || '') as string).getTime()
                                    );

                                  return (
                                    <button
                                      key={sub.id}
                                      type="button"
                                      onClick={() => openCell(st.id, sub.id)}
                                      className="w-full text-left border border-border rounded-lg p-3 bg-card hover:bg-muted/20 transition-colors"
                                    >
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                          <div className="font-medium text-foreground truncate">{sub.name}</div>
                                          {sub.professor_name ? (
                                            <div className="text-xs text-muted-foreground truncate">{sub.professor_name}</div>
                                          ) : null}
                                        </div>
                                        <Icon name="ChevronRight" size={16} className="text-muted-foreground shrink-0 mt-0.5" />
                                      </div>

                                      <div className="mt-2">
                                        {orderedGrades.length ? (
                                          <div className="flex flex-wrap gap-1">
                                            {orderedGrades.map((g) => {
                                              const v = gradeBadgeValue(g);
                                              if (!v) return null;
                                              const meta = gradeBadgeMeta(g);
                                              return (
                                                <span
                                                  key={g.id}
                                                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${meta.cls}`}
                                                  title={meta.title}
                                                >
                                                  <span>{v}</span>
                                                  {meta.short ? <span className="text-[10px] opacity-80">{meta.short}</span> : null}
                                                </span>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <div className="text-xs text-muted-foreground">Fără note</div>
                                        )}

                                        {abs && abs.total > 0 ? (
                                          <div className="text-xs text-muted-foreground mt-1">
                                            Absențe: {abs.total}
                                            {abs.motivated > 0 ? ` (motivate: ${abs.motivated})` : ''}
                                          </div>
                                        ) : null}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>

                              <div className="border-t border-border pt-3 space-y-2">
                                <button
                                  type="button"
                                  onClick={() => openCell(st.id, null)}
                                  className="w-full text-left border border-border rounded-lg p-3 bg-card hover:bg-muted/20 transition-colors"
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="font-medium text-foreground">Purtare</div>
                                    <div className="text-sm text-foreground">
                                      {(() => {
                                        if (!conduct) return '—';
                                        const base = conduct.value != null ? String(conduct.value) : conduct.qualifier || '';
                                        return base || '—';
                                      })()}
                                    </div>
                                  </div>
                                </button>

                                <div className="border border-border rounded-lg p-3 bg-muted/10">
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="font-medium text-foreground">Evoluție</div>
                                    <div className="flex items-center gap-2">
                                      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${evUi.badge}`}>
                                        {evUi.label}
                                      </span>
                                      <Icon name={trendIcon} size={16} className={trendColor} />
                                    </div>
                                  </div>
                                  {evDetail ? <div className="text-xs text-muted-foreground mt-1">{evDetail}</div> : null}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>

                <div className="hidden md:block relative overflow-x-auto border border-border rounded-lg">
                <table className="min-w-[900px] w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="sticky left-0 z-40 bg-muted text-left px-3 py-2 border-b border-border w-[260px] border-r border-border">
                        Elev
                      </th>
                        {displayedSubjects.map((s, idx) => {
                          const isMine = showSubjectLabelsRow && mySubjectIds.has(s.id);
                          const isFirstOther =
                            showSubjectLabelsRow &&
                            subjectView === 'all' &&
                            !isMine &&
                            idx > 0 &&
                            mySubjectIds.has(displayedSubjects[idx - 1]?.id);
                          return (
                            <th
                              key={s.id}
                              className={`text-left px-3 py-2 border-b border-border min-w-[200px] ${
                                isMine ? 'bg-primary/5' : ''
                              } ${isFirstOther ? 'border-l-4 border-primary/30' : ''}`}
                            >
                              <div className="relative" data-subject-menu-root={s.id}>
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0 overflow-hidden">
                                    <div className="font-medium text-foreground truncate">{s.name}</div>
                                    {s.professor_name && (
                                      <div className="text-xs text-muted-foreground truncate">{s.professor_name}</div>
                                    )}
                                  </div>

                                  {isMine && String(me?.role || '').toLowerCase() === 'profesor' && (
                                    <button
                                      type="button"
                                      className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:bg-muted/30"
                                      title="Optiuni materie"
                                      onClick={() => setOpenSubjectMenuId((v) => (v === s.id ? null : s.id))}
                                    >
                                      <Icon name="MoreVertical" size={16} />
                                    </button>
                                  )}
                                </div>

                                {openSubjectMenuId === s.id && (
                                  <div className="absolute right-0 top-8 z-50 w-44 rounded-md border border-border bg-background shadow-md">
                                    <button
                                      type="button"
                                      className="w-full px-3 py-2 text-left text-sm hover:bg-muted/40"
                                      onClick={() => handleRenameSubject(s)}
                                    >
                                      Redenumeste
                                    </button>
                                    <button
                                      type="button"
                                      className="w-full px-3 py-2 text-left text-sm text-error hover:bg-muted/40"
                                      onClick={() => handleDeleteSubject(s)}
                                    >
                                      Sterge
                                    </button>
                                  </div>
                                )}
                              </div>
                            </th>
                          );
                        })}
                       <th className="md:sticky md:right-[220px] z-40 bg-muted text-left px-3 py-2 border-b border-border w-[160px] min-w-[160px] max-w-[160px] border-l border-border md:shadow-[-6px_0_12px_-12px_rgba(0,0,0,0.35)]">
                         Purtare
                       </th>
                         <th className="md:sticky md:right-0 z-40 bg-muted text-left px-3 py-2 border-b border-border w-[220px] min-w-[220px] max-w-[220px] border-l border-border md:shadow-[-6px_0_12px_-12px_rgba(0,0,0,0.35)]">
                          Evoluție
                        </th>
                     </tr>
                   </thead>
                  <tbody>
                    {overview.students.map((st) => (
                      <tr key={st.id} className="group hover:bg-muted/30">
                        <td className="sticky left-0 z-20 bg-background group-hover:bg-muted/30 px-3 py-2 border-b border-border align-top w-[260px] border-r border-border">
                          <div className="font-medium text-foreground">{st.full_name}</div>
                          {st.email && <div className="text-xs text-muted-foreground truncate">{st.email}</div>}
                        </td>
                        {displayedSubjects.map((sub, idx) => {
                          const key = `${st.id}:${sub.id}`;
                          const grades = gradesByCell.get(key) || [];
                          const abs = absByCell.get(key);
                          const orderedGrades = grades
                            .slice()
                            .sort(
                              (a, b) =>
                                new Date((a.date || a.created_at || '') as string).getTime() -
                                new Date((b.date || b.created_at || '') as string).getTime()
                            );
                          const txt = orderedGrades.map(gradeText).filter(Boolean).join(', ');
                          const isMine = showSubjectLabelsRow && mySubjectIds.has(sub.id);
                          const isFirstOther =
                            showSubjectLabelsRow &&
                            subjectView === 'all' &&
                            !isMine &&
                            idx > 0 &&
                            mySubjectIds.has(displayedSubjects[idx - 1]?.id);
                          return (
                            <td
                              key={sub.id}
                              className={`px-3 py-2 border-b border-border align-top cursor-pointer hover:bg-muted/40 ${
                                isMine ? 'bg-primary/5 hover:bg-primary/10' : ''
                              } ${isFirstOther ? 'border-l-4 border-primary/30' : ''}`}
                              onClick={() => openCell(st.id, sub.id)}
                              role="button"
                              tabIndex={0}
                            >
                              {txt ? (
                                <div className="flex flex-wrap gap-1">
                                  {orderedGrades.map((g) => {
                                    const v = gradeBadgeValue(g);
                                    if (!v) return null;
                                    const meta = gradeBadgeMeta(g);
                                    return (
                                      <span
                                        key={g.id}
                                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${meta.cls}`}
                                        title={meta.title}
                                      >
                                        <span>{v}</span>
                                        {meta.short ? <span className="text-[10px] opacity-80">{meta.short}</span> : null}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-muted-foreground">—</div>
                              )}
                              {abs && abs.total > 0 && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  Absențe: {abs.total}
                                  {abs.motivated > 0 ? ` (motivate: ${abs.motivated})` : ''}
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td
                          className="md:sticky md:right-[220px] z-20 bg-background group-hover:bg-muted/30 px-3 py-2 border-b border-border align-top cursor-pointer hover:bg-muted/40 w-[160px] min-w-[160px] max-w-[160px] border-l border-border md:shadow-[-6px_0_12px_-12px_rgba(0,0,0,0.35)]"
                          onClick={() => openCell(st.id, null)}
                          role="button"
                          tabIndex={0}
                        >
                          {(() => {
                            const g = conductByStudent.get(st.id);
                            if (!g) return <div className="text-muted-foreground">—</div>;
                            const base = g.value != null ? String(g.value) : g.qualifier || '';
                            return <div className="text-foreground">{base || '—'}</div>;
                          })()}
                        </td>
                        <td className="md:sticky md:right-0 z-20 bg-background group-hover:bg-muted/30 px-3 py-2 border-b border-border align-top w-[220px] min-w-[220px] max-w-[220px] border-l border-border md:shadow-[-6px_0_12px_-12px_rgba(0,0,0,0.35)]">
                          {(() => {
                            const ev = evolutionByStudent.get(st.id);
                            if (!ev) return <div className="text-muted-foreground">—</div>;

                            const ui =
                              ev.status === 'bine'
                                ? { label: 'Bine', badge: 'bg-success/10 border-success/30 text-success' }
                                : ev.status === 'binisor'
                                ? { label: 'Binișor', badge: 'bg-warning/10 border-warning/30 text-warning' }
                                : ev.status === 'avertisment'
                                ? { label: 'Avertisment', badge: 'bg-error/10 border-error/30 text-error' }
                                : { label: 'Fără date', badge: 'bg-muted/40 border-border text-muted-foreground' };

                            const trendIcon =
                              ev.trend === 'up' ? 'TrendingUp' : ev.trend === 'down' ? 'TrendingDown' : 'Minus';
                            const trendColor =
                              ev.trend === 'up'
                                ? 'text-success'
                                : ev.trend === 'down'
                                ? 'text-error'
                                : 'text-muted-foreground';

                            const detail = [
                              ev.avg != null ? `Medie: ${ev.avg}` : null,
                              `Absențe: ${ev.absTotal}` + (ev.absMotivated ? ` (motivate: ${ev.absMotivated})` : ''),
                            ]
                              .filter(Boolean)
                              .join(' · ');

                            return (
                              <div className="flex items-center gap-2">
                                <span
                                  title={detail}
                                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${ui.badge}`}
                                >
                                  {ui.label}
                                </span>
                                <Icon name={trendIcon} size={16} className={trendColor} />
                                <span className="text-xs text-muted-foreground truncate" title={detail}>
                                  {detail}
                                </span>
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}

            {overview &&
              overview.grades.some((g) => g.is_thesis || g.is_final || g.is_lab || g.is_seminar || g.is_homework) && (
                <div className="mt-2 text-xs text-muted-foreground">
                  FIN = finală • LAB = laborator • TEZ = teză • SEM = seminar • TEM = temă
                </div>
              )}
          </div>
        </div>
      )}

      {cellOpen && overview && selectedConfig && selectedStudent && (
        <CatalogCellModal
          open={cellOpen}
          onClose={() => setCellOpen(false)}
          classroomId={classroomId}
          configId={selectedConfig.id}
          structure={selectedConfig.structure}
          educationLevel={selectedConfig.education_level}
          periodIndex={periodIndex}
          student={selectedStudent}
          subject={
            selectedSubject
              ? { id: selectedSubject.id, name: selectedSubject.name, professor_id: selectedSubject.professor_id ?? null }
              : null
          }
          onChanged={async () => {
            await loadOverview(selectedConfig.id, periodIndex);
          }}
        />
      )}
    </div>
  );
};

export default CatalogPanel;
